"use client";

/**
 * UnifiedMediaSection
 *
 * Routes each media-section mode to its focused implementation while keeping
 * the public component API stable.
 */

import { useEffect } from "react";
import { setStoryBlockPipMessageZIndex } from "../../utils/videoplayer/pipMessageZIndex";
import { extractEncodedIdFromPlayerDetail } from "./hooks/extractEncodedIdFromPlayerDetail";
import { CategorySection } from "./sections/CategorySection";
import { LiveSessionsSection } from "./sections/LiveSessionsSection";
import { PlaylistSection } from "./sections/PlaylistSection";
import type { UnifiedMediaSectionProps } from "./types";

export type { MediaSectionMode, UnifiedMediaSectionProps } from "./types";

/**
 * Renders the section implementation associated with the selected mode.
 */
export default function UnifiedMediaSection(props: Readonly<UnifiedMediaSectionProps>) {
  const { mode } = props;

  useEffect(() => {
    const handlePlayerTransition = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const transitionTo = detail?.transitionTo ?? detail?.to ?? detail?.state;
      const normalizedTransition =
        typeof transitionTo === "string" ? transitionTo.trim().toLowerCase() : "";

      if (normalizedTransition !== "minimized" && normalizedTransition !== "minimized state") {
        return;
      }

      const encodedId = extractEncodedIdFromPlayerDetail(detail);
      setStoryBlockPipMessageZIndex(encodedId);
    };

    document.addEventListener("fw:player:transition", handlePlayerTransition);
    window.addEventListener("fw:player:transition", handlePlayerTransition as EventListener);

    return () => {
      document.removeEventListener("fw:player:transition", handlePlayerTransition);
      window.removeEventListener("fw:player:transition", handlePlayerTransition as EventListener);
    };
  }, []);

  if (mode === "live" || mode === "previous") {
    return <LiveSessionsSection {...props} />;
  }

  if (mode === "upcoming" || mode === "playlist") {
    return <PlaylistSection {...props} />;
  }

  return <CategorySection {...props} />;
}
